import { precedingWindow, trailingWindow } from '~/calculations/calendar'
import { TRAILING_WINDOW_DAYS, type WeightObservation } from '~/calculations/weightTrends'
import type { WeightEntryRecord } from '~/models/weightEntry'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import {
  getWeightEntry,
  listWeightEntriesInRange,
  putWeightEntry,
} from '~/storage/repositories/weightRepository'
import { type CalendarDate, calendarDateFrom } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'

/**
 * Daily weight entry.
 *
 * The calendar date is resolved from the caller's `now` using the LOCAL date
 * (D-034), never from a UTC conversion — that would attribute a late-evening
 * weigh-in to the following day for anyone behind Greenwich.
 */
export function todayFor(now: Date): CalendarDate {
  return calendarDateFrom(now)
}

export interface SaveWeightInput {
  readonly date: CalendarDate
  readonly weightKg: number
  readonly note?: string
}

/**
 * Save a weight for a calendar date, stamping the active phase.
 *
 * The active phase is read BEFORE the write rather than inside it, keeping any
 * non-trivial work out of the transaction.
 */
export async function saveWeight(
  db: SolaDatabase,
  input: SaveWeightInput,
  now: Date,
): Promise<Result<WeightEntryRecord, StorageError>> {
  const phase = await getActivePhase(db)
  const phaseId = phase.ok ? phase.value?.id : undefined

  return putWeightEntry(
    db,
    {
      date: input.date,
      weightKg: input.weightKg,
      ...(input.note === undefined ? {} : { note: input.note }),
    },
    now,
    phaseId,
  )
}

/**
 * Observations covering the current and previous trailing windows.
 *
 * Reads a fourteen-day span so both windows the trend needs are present. The
 * calculation itself stays pure — this only fetches.
 */
export async function getTrendObservations(
  db: SolaDatabase,
  reference: CalendarDate,
): Promise<Result<readonly WeightObservation[], StorageError>> {
  const current = trailingWindow(reference, TRAILING_WINDOW_DAYS)
  const previous = precedingWindow(current, TRAILING_WINDOW_DAYS)

  const entries = await listWeightEntriesInRange(db, previous.from, current.to)
  if (!entries.ok) return entries

  return ok(entries.value.map((entry) => ({ date: entry.date, weightKg: entry.weightKg })))
}

export interface DailyStatus {
  readonly date: CalendarDate
  readonly today: WeightEntryRecord | undefined
  readonly yesterday: WeightEntryRecord | undefined
  /** True when yesterday has no entry. Drives neutral, non-punitive copy only. */
  readonly yesterdayMissing: boolean
}

/**
 * Today's entry plus whether yesterday's is missing.
 *
 * Used only to show the approved neutral message. There is no streak, no
 * penalty, and no failure state for an ordinary omission.
 */
export async function getDailyStatus(
  db: SolaDatabase,
  now: Date,
): Promise<Result<DailyStatus, StorageError>> {
  const date = todayFor(now)

  const previous = new Date(now.getTime())
  previous.setDate(previous.getDate() - 1)
  const yesterdayDate = calendarDateFrom(previous)

  const today = await getWeightEntry(db, date)
  if (!today.ok) return today

  const yesterday = await getWeightEntry(db, yesterdayDate)
  if (!yesterday.ok) return yesterday

  return ok({
    date,
    today: today.value,
    yesterday: yesterday.value,
    yesterdayMissing: yesterday.value === undefined,
  })
}
